import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SignupPageShell from './SignupPageShell';
import { Toast } from 'src/components/shared/NotificationToast';
import { UserContext } from './auth/UserContext';
import {
  clearSignupToken,
  getPostSignupRedirect,
  getStoredSignupToken,
  persistLoggedInUser,
} from './auth/signupOnboarding';

const formatDateTime = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const deriveProviderSubscriptionState = (status) => {
  const rawProviderState = String(status?.payment?.providerSubscriptionStatus || '').trim();
  if (rawProviderState) {
    if (status?.selectedPlan?.category === 'free_trial' && rawProviderState === 'authenticated') {
      return 'Card authenticated';
    }
    return rawProviderState;
  }
  if (status?.selectedPlan?.category === 'free_trial') {
    if (status?.activationStatus === 'activated' || status?.payment?.status === 'paid') {
      return 'Trial scheduled';
    }
  }
  return 'Not started';
};

const resolveVerificationAlert = (status, retryRequired) => {
  if (status?.activationStatus === 'activated') return null;
  if (status?.payment?.status === 'failed') return null;
  if (status?.payment?.status === 'cancelled') return null;
  if (status?.payment?.status === 'expired' || status?.onboardingStatus === 'expired') return null;
  if (retryRequired) return null;
  if (!status?.verificationMessage) return null;

  return {
    severity: status.verificationMessageSeverity || 'info',
    message: status.verificationMessage,
  };
};

const isTerminalSignupState = (status) => (
  status?.activationStatus === 'activated'
  || status?.payment?.status === 'failed'
  || status?.payment?.status === 'cancelled'
  || status?.payment?.status === 'expired'
  || status?.onboardingStatus === 'expired'
);

const RegisterPaymentStatus = () => {
  const navigate = useNavigate();
  const { setUser } = useContext(UserContext);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [finalizing, setFinalizing] = useState(false);
  const finalizedRef = useRef(false);
  const signupToken = getStoredSignupToken();

  const finalizeActivatedSignup = useCallback(async () => {
    if (finalizedRef.current) return;
    finalizedRef.current = true;
    setFinalizing(true);
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/auth/register/finalize`, {
        signupToken,
      });
      clearSignupToken();
      persistLoggedInUser({ data, setUser });
      navigate(getPostSignupRedirect(data.user), { replace: true });
    } catch (error) {
      finalizedRef.current = false;
      Toast({ type: 'error', message: error.response?.data?.message || error.message });
    } finally {
      setFinalizing(false);
    }
  }, [navigate, setUser, signupToken]);

  const fetchStatus = useCallback(async () => {
    if (!signupToken) {
      Toast({ type: 'error', message: 'No signup session found.' });
      navigate('/auth/register', { replace: true });
      return;
    }
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/auth/register/status`, {
        params: { signupToken },
      });
      setStatus(data);
      if (data.activationStatus === 'activated') {
        await finalizeActivatedSignup();
      }
    } catch (error) {
      Toast({ type: 'error', message: error.response?.data?.message || error.message });
    } finally {
      setLoading(false);
    }
  }, [finalizeActivatedSignup, navigate, signupToken]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    if (!signupToken) return undefined;
    if (isTerminalSignupState(status)) return undefined;
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      fetchStatus();
    }, 5000);
    return () => window.clearInterval(timer);
  }, [fetchStatus, signupToken, status]);

  useEffect(() => {
    if (!signupToken) return undefined;
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !isTerminalSignupState(status)) {
        fetchStatus();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchStatus, signupToken, status]);

  if (loading) {
    return (
      <SignupPageShell title="Payment Status" description="Check payment status">
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      </SignupPageShell>
    );
  }

  const trialEndsAt = formatDateTime(status?.trialEndsAt);
  const paymentExpiresAt = formatDateTime(status?.payment?.expiresAt);
  const providerPlanName = status?.providerBackedPlan?.planName;
  const providerPlanDisplayName = status?.selectedPlan?.category === 'free_trial' && providerPlanName
    ? `${providerPlanName} (scheduled after trial)`
    : providerPlanName;
  const providerSubscriptionState = deriveProviderSubscriptionState(status);
  const retryRequired = Boolean(status?.retryRequired);
  const verificationAlert = resolveVerificationAlert(status, retryRequired);

  return (
    <SignupPageShell title="Subscription Status" description="Track subscription verification">
      <Typography variant="h2" fontWeight="700" mb={1}>
        Subscription Status
      </Typography>
      <Typography color="text.secondary" mb={3}>
        This page tracks Razorpay subscription authentication, verification, webhooks, and account activation automatically.
      </Typography>

      {status?.activationStatus === 'activated' ? (
        <Alert severity="success" sx={{ mb: 3 }}>
          Subscription confirmed. Finalizing your dashboard access...
        </Alert>
      ) : null}

      {status?.payment?.status === 'failed' ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          Subscription authentication failed. {status.payment.failureReason || 'Please retry the checkout.'}
        </Alert>
      ) : null}

      {retryRequired ? (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {status?.payment?.failureReason || 'An older or mismatched checkout was detected. Retry using the latest subscription checkout.'}
        </Alert>
      ) : null}

      {status?.payment?.status === 'cancelled' ? (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Checkout was cancelled. You can retry subscription authentication for the selected plan.
        </Alert>
      ) : null}

      {status?.payment?.status === 'expired' || status?.onboardingStatus === 'expired' ? (
        <Alert severity="warning" sx={{ mb: 3 }}>
          This signup session has expired. Start over to continue.
        </Alert>
      ) : null}

      {verificationAlert ? (
        <Alert severity={verificationAlert.severity} sx={{ mb: 3 }}>
          {verificationAlert.message}
        </Alert>
      ) : null}

      <Stack spacing={1} mb={3}>
        <Typography><strong>Onboarding:</strong> {status?.displayOnboardingStatus || 'Status unavailable'}</Typography>
        <Typography><strong>Activation:</strong> {status?.displayActivationStatus || 'Status unavailable'}</Typography>
        <Typography><strong>Plan:</strong> {status?.displayPlanName || 'Not selected'}</Typography>
        {status?.selectedPlan?.category === 'free_trial' && providerPlanName ? (
          <Typography><strong>Paid Plan After Trial:</strong> {providerPlanName}</Typography>
        ) : null}
        {providerPlanDisplayName ? (
          <Typography><strong>Provider Plan:</strong> {providerPlanDisplayName}</Typography>
        ) : null}
        <Typography><strong>Subscription:</strong> {status?.payment?.displayStatus || 'Not started'}</Typography>
        <Typography><strong>Provider State:</strong> {providerSubscriptionState}</Typography>
        {status?.payment?.razorpaySubscriptionId ? (
          <Typography sx={{ wordBreak: 'break-all' }}><strong>Subscription ID:</strong> {status.payment.razorpaySubscriptionId}</Typography>
        ) : null}
        {status?.payment?.razorpayPaymentId ? (
          <Typography sx={{ wordBreak: 'break-all' }}><strong>Payment ID:</strong> {status.payment.razorpayPaymentId}</Typography>
        ) : null}
        {trialEndsAt ? (
          <Typography><strong>Trial Ends:</strong> {trialEndsAt}</Typography>
        ) : null}
        {paymentExpiresAt ? (
          <Typography><strong>Checkout Expires:</strong> {paymentExpiresAt}</Typography>
        ) : null}
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
         <Button variant="outlined" component={Link} to="/auth/register/plans">
          View Plans
        </Button>
        <Button
          variant="contained"
          onClick={fetchStatus}
          disabled={finalizing}
        >
          Refresh Status
        </Button>    
        {((status?.retryEligible || retryRequired) && status?.onboardingStatus !== 'expired') ? (
          <Button variant="outlined" component={Link} to="/auth/register/plans">
            Retry Checkout
          </Button>
        ) : null}
        {(status?.payment?.status === 'expired' || status?.onboardingStatus === 'expired') ? (
          <Button variant="outlined" component={Link} to="/auth/register" onClick={clearSignupToken}>
            Start Again
          </Button>
        ) : null}
      </Stack>
    </SignupPageShell>
  );
};

export default RegisterPaymentStatus;
